import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import axiosInstance from '../../utility/axiosInstance';
import {handleAxiosError} from '../../helpers/handleAxiosError';
import {Snapshot} from "./snapshotsSlice";

export interface Backup {
    id: string;
    name: string;
    status: string;
    size: number;
    volume_id: string;
    created_at: string;
}

interface BackupsState {
    backups: Backup[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: BackupsState = {
    backups: [],
    status: 'idle',
    error: null,
};

export const fetchBackups = createAsyncThunk(
    'backups/fetchBackups',
    async (_, {rejectWithValue}) => {

        try {
            const response = await axiosInstance.get('/v1/backups');

            if (!Array.isArray(response.data)) {
                throw new Error("Expected array of backups");
            }

            return response.data;
        } catch (error) {
            console.error("Failed to fetch backups:", error);
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

const backupsSlice = createSlice({
    name: 'backups',
    initialState,
    reducers: {
        resetBackups: () => initialState,
        addBackup: (state, action: PayloadAction<Backup>) => {
            state.backups.push(action.payload);
        },
        removeBackup: (state, action: PayloadAction<{ id: string }>) => {
            state.backups = state.backups.filter(backup => backup.id !== action.payload.id);
        },
        updateBackup: (state, action: PayloadAction<Backup>) => {
            if (!action.payload || !action.payload.id) {
                console.warn("updateBackup called with invalid payload", action.payload);
                return;
            }
            const index = state.backups.findIndex(
                (backup) => backup.id === action.payload.id
            );

            if (index !== -1) {
                state.backups[index] = action.payload;
            } else {
                state.backups.push(action.payload);
            }
        },
        setBackupStatus: (state, action: PayloadAction<{ id: string; status: string }>) => {
            const backup = state.backups.find(s => s.id === action.payload.id);
            if (backup) {
                backup.status = action.payload.status;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBackups.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchBackups.fulfilled, (state, action: PayloadAction<Backup[]>) => {
                state.status = 'succeeded';
                state.backups = action.payload.map(backup => ({
                    id: backup.id,
                    name: backup.name,
                    status: backup.status,
                    size: backup.size || 0,
                    volume_id: backup.volume_id,
                    created_at: backup.created_at,
                }));
            })
            .addCase(fetchBackups.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.backups = [];
            });
    },
});

export const {resetBackups, addBackup, removeBackup, updateBackup, setBackupStatus} = backupsSlice.actions;
export default backupsSlice.reducer;